import { debounce } from "@mui/material";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const StateContext = createContext();

export const ContextProvider = ({ children }) => {
  const BACKEND_URL = process.env.REACT_APP_BASE_URL;
  const MARKER = process.env.GSAP_MARKER || "false";
  //   const [currentMode, setCurrentMode] = useState("light");
  // const [activeMenu, setActiveMenu] = useState(true);
  const [allRoutes, setAllRoutes] = useState([]);
  const [langCode, setLangCode] = useState("en");
  const [fontFam, setFontFam] = useState("CustomFont");
  const [isMobile, setIsMobile] = useState(false);
  const [deviceSize, setDeviceSize] = useState("sm");

  const [userInfo, setUserInfo] = useState({
    ip: null,
    userAgent: null,
    countryCode: null,
    countryName: null,
  });

  // DEVICE SIZE
  const breakpoints = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
  };

  // LANGUAGE
  const { t, i18n } = useTranslation();

  // AVAILABLE LANGS
  const langs = [
    {
      code: "en",
      name: "English",
      title: "English",
      rtl: false,
      flag: "/assets/images/flags/english-flag.png",
      font: "CustomFont",
      size: "12px",
    },
    {
      code: "ar",
      name: "Arabic",
      title: "عربي",
      rtl: true,
      flag: "/assets/images/flags/arabic-flag.png",
      font: "ArabicFont",
      size: "12px",
    },
  ];

  // RETRIEVE LANGUAGE DETAILS
  const getLangDetails = (langCode) => {
    const language = langs.find((lang) => lang.code === langCode);
    if (language) {
      const { title, flag, font, rtl } = language;
      document.body.style.direction = rtl ? "rtl" : "ltr";
      // document.body.style.setProperty("font-family", font);
      document.body.style.fontFamily = font;
      setFontFam(font);
      return { title, flag };
    }
    return null;
  };

  // CHECK IF LANGUAGE IS RTL
  const isLangRTL = (langCode) => {
    const language = langs.find((lang) => lang.code === langCode);
    return language ? language.rtl : false;
  };

  // CHANGE LANGUAGE
  const changeLanguage = (newLangCode) => {
    setLangCode(newLangCode);
    i18n.changeLanguage(newLangCode);
    getLangDetails(newLangCode);
  };

  // CHECK URL PATH TO SET LANGUAGE
  useEffect(() => {
    const path = window.location.pathname;
    const langFromUrl = path.split("/")[1];
    if (langFromUrl === "ar") {
      changeLanguage("ar");
    } else {
      const langExists = langs.find((lang) => lang.code === langFromUrl);
      if (langExists) {
        changeLanguage(langFromUrl);
      } else {
        changeLanguage("en");
      }
    }
  }, []);

  // APPLY LANGUAGE DETAILS ON INITIAL LOAD
  useEffect(() => {
    getLangDetails(langCode);
  }, [langCode]);

  // CHECK IF TEXT CONTAINS ARABIC CHARACTER
  const isArabic = (text) => {
    const regex = new RegExp(
      "[\u0600-\u06FF\u0750-\u077F\u08a0-\u08ff\uFB50-\uFDFF\uFE70-\uFEFF]+([\u0600-\u06FF\u0750-\u077F\u08a0-\u08ff\uFB50-\uFDFF\uFE70-\uFEFF\\W\\d]+)*",
      "g"
    );
    if (text) {
      return text.match(regex);
    } else {
      return false;
    }
  };

  // CHECK IF TEXT CONTAINS ENGLISH CHARACTER
  const isEnglish = (text) => {
    const regex = new RegExp("^[\u0000-\u007F]+$", "g");
    if (text) {
      return text.match(regex);
    } else {
      return false;
    }
  };

  // FETCH USER INFO
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // USER AGENT
        const userAgent = navigator.userAgent;
        // IP AND LOCATION
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();

        setUserInfo({
          ip: data.ip,
          userAgent,
          countryCode: data.country_code,
          countryName: data.country_name,
        });
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };
    fetchUserInfo();
    // console.log("USER INFO::: ", userInfo);
  }, []);

  useEffect(() => {
    if (userInfo.ip && userInfo.countryName) {
      console.log("USER INFO::: ", userInfo);
    }
  }, [userInfo]);

  // DEVICE TYPE WITH WIDTH SIZE
  useEffect(() => {
    const determineDevice = () => {
      const width = window.innerWidth;

      if (width < 768) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };
    determineDevice();
    window.addEventListener("resize", determineDevice);

    return () => {
      window.removeEventListener("resize", determineDevice);
    };
  }, []);
  useEffect(() => {
    console.log("IS MOBILE: ", isMobile);
  }, [isMobile]);

  // DEVICE SIZE 
  const getDeviceSize = (width) => {
    if (width >= breakpoints.xl) return "xl";
    if (width >= breakpoints.lg) return "lg";
    if (width >= breakpoints.md) return "md";
    if (width >= breakpoints.sm) return "sm";
    return "xs";
  }
  useEffect(() => {
    const handleResize = debounce(() => {
      setDeviceSize(getDeviceSize(window.innerWidth));
    }, 100);

    window.addEventListener("resize", handleResize);
    handleResize();

    // Cleanup on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    console.log("Device size:", deviceSize);
  }, [deviceSize]);

  return (
    <StateContext.Provider
      value={{
        BACKEND_URL,
        MARKER,
        langs,
        langCode,
        setLangCode,
        allRoutes,
        setAllRoutes,
        isArabic,
        isEnglish,
        isLangRTL,
        getLangDetails,
        fontFam,
        setFontFam,
        t,
        i18n,
        changeLanguage,
        userInfo,
        setUserInfo,
        isMobile,
        setIsMobile,
        deviceSize,
        setDeviceSize
        // currentMode,
        // setCurrentMode,
        // activeMenu,
        // setActiveMenu,
      }}
    >
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = () => useContext(StateContext);
